import LegalPage from './LegalPage'

export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="April 8, 2026">
      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mb-3">
        1. Introduction
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        This Privacy Policy explains how GCodr ("we", "us", "our") collects, uses, and protects your information when you use our Service. By using GCodr, you consent to the practices described in this policy.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        2. Information We Collect
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        We collect the following types of information:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li><strong className="text-slate-300">Account information:</strong> Email address and name provided during registration via Supabase Auth.</li>
        <li><strong className="text-slate-300">Uploaded files:</strong> Engineering drawings you upload (DWG, DXF, STEP, PDF formats).</li>
        <li><strong className="text-slate-300">Generated data:</strong> G-code programs, jobs, toolpaths, and chat messages created through the Service.</li>
        <li><strong className="text-slate-300">Machine profiles and tool inventories:</strong> CNC machine configurations and tool libraries you create.</li>
        <li><strong className="text-slate-300">Usage data:</strong> Pages visited, features used, and interactions with the Service.</li>
        <li><strong className="text-slate-300">Technical data:</strong> Browser type, operating system, IP address, and device information.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        3. How We Use Your Information
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        We use your information to:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>Provide and operate the Service, including analyzing your drawings and generating G-code.</li>
        <li>Process your engineering drawings through our AI pipeline.</li>
        <li>Maintain your account and authenticate your sessions.</li>
        <li>Improve the Service through anonymized, aggregated usage analysis.</li>
        <li>Communicate with you about service updates or respond to support inquiries.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        4. Third-Party Services
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        GCodr uses the following third-party services to operate. Your data may be transmitted to these providers:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li><strong className="text-slate-300">OpenRouter:</strong> AI and large language model requests. Drawing data and chat messages are sent to LLM providers through OpenRouter for analysis and G-code generation.</li>
        <li><strong className="text-slate-300">Supabase:</strong> Authentication and database storage for your account, jobs, and machine profiles.</li>
        <li><strong className="text-slate-300">Vercel:</strong> Frontend hosting and delivery.</li>
        <li><strong className="text-slate-300">Railway:</strong> Backend API hosting.</li>
        <li><strong className="text-slate-300">Fly.io:</strong> CAM processing service hosting.</li>
      </ul>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        Each third-party service operates under its own privacy policy. We encourage you to review their policies.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        5. Cookies and Tracking
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        GCodr uses authentication tokens stored in localStorage to maintain your session. We do not use advertising cookies or third-party tracking pixels. We may use basic analytics to understand how the Service is used.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        6. Data Retention
      </h2>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li><strong className="text-slate-300">Account data:</strong> Retained while your account is active. Deleted upon account deletion request.</li>
        <li><strong className="text-slate-300">Uploaded files and generated data:</strong> Retained while your account is active and accessible for your use.</li>
        <li><strong className="text-slate-300">Server logs:</strong> Retained for up to 90 days for debugging and security purposes.</li>
        <li><strong className="text-slate-300">Anonymized data:</strong> Aggregated, de-identified data may be retained indefinitely for service improvement.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        7. Data Security
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        We take reasonable measures to protect your information:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>All data is transmitted over HTTPS/TLS encrypted connections.</li>
        <li>Authentication is handled by Supabase Auth with industry-standard security practices.</li>
        <li>Access to production systems is restricted to authorized personnel.</li>
        <li>We do not store raw passwords — authentication uses secure token-based flows.</li>
      </ul>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        However, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        8. Your Rights
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        You have the right to:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li><strong className="text-slate-300">Access:</strong> Request a copy of the personal data we hold about you.</li>
        <li><strong className="text-slate-300">Deletion:</strong> Request deletion of your account and associated data.</li>
        <li><strong className="text-slate-300">Correction:</strong> Request correction of inaccurate personal data.</li>
        <li><strong className="text-slate-300">Export:</strong> Request an export of your data in a portable format.</li>
      </ul>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        To exercise any of these rights, contact us at{' '}
        <a href="mailto:support@gcodr.dev" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">
          support@gcodr.dev
        </a>.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        9. Children's Privacy
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        GCodr is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a minor, please contact us and we will promptly delete it.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        10. Changes to This Policy
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        We may update this Privacy Policy from time to time. Changes will be reflected in the "last updated" date at the top of this page. We encourage you to review this policy periodically.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        11. Contact
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        If you have questions about this Privacy Policy, contact us at{' '}
        <a href="mailto:support@gcodr.dev" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">
          support@gcodr.dev
        </a>.
      </p>
    </LegalPage>
  )
}
